/**
 * "Re-seed global tripcode app screenshots" control.
 *
 * Used in two places:
 *   - Campaigns list header — so admin can seed the global pool
 *     before creating their first campaign.
 *   - Each campaign's Assets tab header — so admin can re-run a
 *     failed seed or pick up new screenshots from the landing-page
 *     repo after it ships an update.
 *
 * Hits POST /api/marketing/global-assets/seed-screenshots. The
 * endpoint is idempotent (de-dupes by metadata.app_screenshot_path),
 * so a stray double-click can't duplicate the pack.
 */

import { useState } from 'react';
import { apiJson } from '../lib/api';

export function ScreenshotsResync({ onSeeded }: { onSeeded?: () => void }) {
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function run() {
    setBusy(true);
    setError(null);
    setSummary(null);
    try {
      const res = await apiJson<{
        seeded: number;
        skipped: number;
        errors: { path: string; message: string }[];
        total: number;
      }>(`/api/marketing/global-assets/seed-screenshots`, { method: 'POST' });
      const parts: string[] = [];
      if (res.seeded > 0) parts.push(`${res.seeded} added`);
      if (res.skipped > 0) parts.push(`${res.skipped} already present`);
      if (res.errors.length > 0) parts.push(`${res.errors.length} failed`);
      setSummary(parts.length > 0 ? parts.join(' · ') : 'no changes');
      if (res.seeded > 0) onSeeded?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Re-seed failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        gap: 8,
        alignItems: 'center',
        flexWrap: 'wrap',
        fontSize: 12,
        color: 'var(--color-text-muted)',
      }}
    >
      <span>tripcode app screenshots — global pool, shared across all campaigns —</span>
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        disabled={busy}
        onClick={run}
        title="Re-fetch the tripcode app screenshot pack from the landing-page deploy into the global pool"
      >
        {busy ? 'Re-seeding…' : 'Re-seed'}
      </button>
      {summary && <span style={{ color: 'var(--color-text)' }}>{summary}</span>}
      {error && <span style={{ color: 'crimson' }}>{error}</span>}
    </div>
  );
}
